import { App as AntdApp, message as staticMessage } from "antd";
import { useEffect, useId, useRef, type ReactNode } from "react";
import { Alert as UiKitAlert, type AlertProps as UiKitAlertProps } from "zhihao-ui-react";

/** 兼容迁移页面的 Alert 属性，同时为全局消息保留 message/title 两种旧文案写法。 */
export type PageAlertProps = Omit<UiKitAlertProps, "title"> & {
    message?: ReactNode;
    title?: ReactNode;
};

/** 页面反馈统一使用旧版 Vue ElMessage 的全局消息，避免错误提示挤占页面内容。 */
export const PageAlert = ({ message: alertMessage, title, description, type = "info", action, ...props }: PageAlertProps) => {
    const appMessage = AntdApp.useApp().message;
    // 测试或独立挂载场景可能没有 AntdApp，生产页面则使用根节点提供的上下文消息实例。
    const messageApi = typeof appMessage.open === "function" ? appMessage : staticMessage;
    const alertTitle = title ?? alertMessage;
    // Ant Alert 允许仅传 description；UI Kit 需将其放入标题位，避免出现组件默认占位标题。
    const hasDescriptionOnly = alertTitle == null && description != null;
    const content = hasDescriptionOnly ? description : alertTitle;
    const messageKey = useId();
    const signature = `${type}|${String(content ?? "")}|${String(description ?? "")}|${Boolean(action)}`;
    const lastSignature = useRef<string | undefined>(undefined);
    const shouldNotify = type === "error" || type === "success" || props.role === "alert";

    useEffect(() => {
        if (!shouldNotify || content == null) {
            messageApi.destroy(messageKey);
            return;
        }
        if (lastSignature.current === signature && !action) return;
        lastSignature.current = signature;

        const messageType: "info" | "success" | "warning" | "error" =
            type === "primary" ? "info" : type === "info" || type === "success" || type === "warning" ? type : "error";
        // 部分迁移页面已同时调用 antd message；复用已有同文案提示，避免一次操作弹出两条消息。
        const contentText = typeof content === "string" || typeof content === "number" ? String(content) : "";
        const hasExistingMessage =
            contentText !== "" &&
            typeof document !== "undefined" &&
            [...document.querySelectorAll(".ant-message-notice")].some(notice =>
                notice.textContent?.includes(contentText)
            );
        if (hasExistingMessage) return;
        const messageContent = (
            <span>
                {content}
                {description != null && !hasDescriptionOnly ? <span>{description}</span> : null}
                {action ? <span onClick={() => messageApi.destroy(messageKey)}>{action}</span> : null}
            </span>
        );
        messageApi.open({
            key: messageKey,
            type: messageType,
            content: messageContent,
            duration: action ? 0 : 3
        });
    }, [action, content, description, hasDescriptionOnly, messageApi, messageKey, shouldNotify, signature, type]);

    useEffect(() => () => messageApi.destroy(messageKey), [messageApi, messageKey]);

    // 页面只保留无视觉语义节点，避免原有错误状态测试和读屏用户失去反馈。
    if (shouldNotify) {
        return content == null ? null : (
            <div
                className="zh-alert page-alert-message-a11y"
                role="alert"
                style={{
                    position: "absolute",
                    width: 1,
                    height: 1,
                    padding: 0,
                    margin: -1,
                    overflow: "hidden",
                    clip: "rect(0, 0, 0, 0)",
                    whiteSpace: "nowrap",
                    border: 0
                }}
            >
                {content}
                {description != null && !hasDescriptionOnly ? description : null}
            </div>
        );
    }

    return (
        <UiKitAlert
            {...props}
            title={hasDescriptionOnly ? description : alertTitle}
            description={hasDescriptionOnly ? undefined : description}
            type={type}
        />
    );
};
